import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {HealthDeclaration, HealthDeclarationQuestion, AnsweredHealthDeclaration} from 'src/services/api/types/types';
import {fetchHealthDeclaration, fetchUserHealthdecl} from 'src/services/api/api';
import {
    DataActions,
    useArrivalContext,
    Person,
    HealthDeclarationAnswers,
    PersonAnswers
} from 'src/context/ArrivalContext';
import {Button} from 'src/components/form/button/Button';
import {Radio} from 'src/components/form/radio/Radio';
import {getTextFromLocale} from 'src/services/i18n/locale';
import {Spinner} from 'src/components/layout/loader/Spinner';
import {format} from '../../../../lib/dates/format';
import './DropinModalHD.scss';
import {DropinTravel} from './DropinTravel';
import {useSessionContext} from 'src/context/SessionContext';

export function DropinModalHD() {
    const {t} = useTranslation();
    const {dispatch, dataState} = useArrivalContext();
    const [healthDeclaration, setHealthDeclaration] = useState<HealthDeclaration | null>(null);
    const [loadStatus, setLoadStatus] = useState<'loading' | 'done' | 'error'>('loading');
    const [formState, setFormState] = useState<PersonAnswers | null>(null);
    const [loadedHDs, setLoadedHDs] = useState<Array<AnsweredHealthDeclaration | null | undefined>>([]);
    const [loadHDClick, setLoadHDClick] = useState(new Set());
    const [openPersons, setOpenPersons] = useState<{[identity: string]: 'open' | 'closed'}>({});
    const [editPersons, setEditPersons] = useState<{[identity: string]: boolean}>({});
    const {sessionState} = useSessionContext();

    const {userJwt} = sessionState;

    useEffect(() => {
        const load = async () => {
            try {
                let hd;
                let oldHDs;
                if (sessionState.hasActiveSession) {
                    const promises = dataState.persons.map((p) => {
                        if (p.id) {
                            return fetchUserHealthdecl(
                                p.id,
                                dataState.reasonState.healthDeclarationDefinitionId as number,
                                userJwt
                            );
                        }
                    });

                    [hd, oldHDs] = await Promise.all([
                        await fetchHealthDeclaration(dataState.reasonState.healthDeclarationDefinitionId as number),
                        await Promise.all(promises)
                    ]);

                    setLoadedHDs(oldHDs);
                } else {
                    [hd] = await Promise.all([
                        await fetchHealthDeclaration(dataState.reasonState.healthDeclarationDefinitionId as number)
                    ]);
                }

                setHealthDeclaration(hd);
                setLoadStatus('done');
            } catch (error) {
                // eslint-disable-next-line no-console
                console.log(error);
                setLoadStatus('error');
            }
        };

        load();
    }, [dataState.reasonState.healthDeclarationDefinitionId]);

    const fillOldHD = (identity: string) => {
        const oldAnswers: Record<string, HealthDeclarationAnswers> = formState ? {...formState.answers} : {};

        loadedHDs?.forEach((hd, index) => {
            if (!hd) return;

            const p = dataState.persons[index];
            if (identity != p.identity) return;

            const answerMap: HealthDeclarationAnswers = {};

            hd.answers.forEach(({questionId, answer}) => {
                answerMap[questionId] = answer === 'yes' ? true : false;
            });

            oldAnswers[identity] = answerMap;
        });

        setFormState({
            ...formState,
            answers: oldAnswers
        });

        //make sure questions dont auto close
        setEditPersons((prev) => {
            const current = prev[identity] ?? false;
            const next = current === false ? true : false;

            return {
                ...prev,
                [identity]: next
            };
        });

        setLoadHDClick((prev) => new Set(prev).add(identity));
    };

    const haveOldHD = (identity: string): boolean => {
        const index = dataState.persons.findIndex((p) => p.identity === identity);
        if (index === -1) return false;

        return loadedHDs[index] != null;
    };

    const isApplicable = (person: Person, {applicableTo, minAge, maxAge}: HealthDeclarationQuestion) => {
        if (person.age) {
            if (minAge && person.age < minAge) return false;
            if (maxAge && person.age > maxAge) return false;
        }
        if (applicableTo === 'all') {
            return true;
        }
        if (person.gender === 'female' && applicableTo === 'women') {
            return true;
        }
        return person.gender === 'male' && applicableTo === 'men';
    };

    const getApplicableQuestions = (questions: Array<HealthDeclarationQuestion>, person: Person) => {
        const applicableQuestions = questions
            .filter((question) => isApplicable(person, question))
            .map((question) => {
                return {
                    ...question,
                    followUpQuestions: question.followUpQuestions?.filter((question) => isApplicable(person, question))
                };
            });

        return applicableQuestions;
    };

    const updateAnswer = (
        state: PersonAnswers | null,
        personId: string,
        questionId: number,
        value: boolean | null
    ): PersonAnswers => {
        const existingAnswers = state?.answers ?? {};
        const currentPersonAnswers = existingAnswers[personId] ?? {};

        const updatedPersonAnswers: HealthDeclarationAnswers = {
            ...currentPersonAnswers,
            [questionId]: value
        };

        const updatedAnswers = {
            ...existingAnswers,
            [personId]: updatedPersonAnswers
        };

        return {
            answers: updatedAnswers
        };
    };

    const togglePersonQuestions = (identity: string) => {
        setOpenPersons((prev) => {
            const current = prev[identity] ?? 'closed';
            const next = current === 'open' ? 'closed' : 'open';

            return {
                ...prev,
                [identity]: next
            };
        });
        if (openPersons[identity] != undefined) {
            setEditPersons((prev) => {
                const current = prev[identity] ?? false;
                const next = current === false ? true : false;

                return {
                    ...prev,
                    [identity]: next
                };
            });
        }
    };

    const personAllAnswers = (p: Person) => {
        if (!healthDeclaration) return false;

        const applicableQuestions = getApplicableQuestions(healthDeclaration.questions, p);
        const answers = formState?.answers?.[p.identity] ?? {};

        const allAnswered = applicableQuestions.every((q) => {
            const answer = answers[q.id];
            const hasAnswer = answer !== null && answer !== undefined;

            if (!hasAnswer) return false;

            const devAnswer = q.deviatingAnswer === 'yes';

            const triggersFollowUps = devAnswer !== undefined && answer === devAnswer;

            if (triggersFollowUps && Array.isArray(q.followUpQuestions)) {
                return q.followUpQuestions.every((fq) => {
                    const followUpAnswer = answers[fq.id];
                    return followUpAnswer !== null && followUpAnswer !== undefined;
                });
            }

            return true;
        });

        return allAnswered;
    };

    const persons = dataState.persons.map((p, i) => {
        let questions;
        let oldHDButton;

        let isVisible = openPersons[p.identity] === 'open' || editPersons[p.identity] === true;
        let allAnswered = false;

        if (healthDeclaration) {
            const applicableQuestions = getApplicableQuestions(healthDeclaration.questions, p);

            allAnswered = personAllAnswers(p);

            // Always start in open state
            if (!isVisible && !allAnswered) {
                isVisible = true;
                togglePersonQuestions(p.identity); // set state to prevent having to click toggle button twice
            }

            if (
                allAnswered &&
                openPersons[p.identity] === 'open' &&
                (editPersons[p.identity] === false || editPersons[p.identity] === undefined)
            ) {
                isVisible = false;
            }

            questions = applicableQuestions.map((q, i) => {
                return (
                    <div className='arrival-dropin-hd-question' key={q.id}>
                        <div className='arrival-dropin-hd-question__text'>{getTextFromLocale(q.text)}</div>
                        <div className='arrival-dropin-hd-question__answer'>
                            <Radio
                                checked={
                                    (formState &&
                                        formState['answers'][p.identity] &&
                                        formState['answers'][p.identity][q.id] === true) ??
                                    false
                                }
                                name={`q-name-${p.identity}-${q.id}`}
                                label={t('button_yes.text')}
                                onClick={() => {
                                    let clearedFollowups = formState;

                                    if (formState && q.deviatingAnswer == 'no') {
                                        const personAnswers = formState.answers[p.identity] ?? {};

                                        const filteredAnswers = Object.fromEntries(
                                            Object.entries(personAnswers).filter(([key]) => {
                                                return !q.followUpQuestions?.some(({id}) => id === Number(key));
                                            })
                                        );

                                        clearedFollowups = {
                                            ...formState,
                                            answers: {...formState.answers, [p.identity]: filteredAnswers}
                                        };
                                    }

                                    const newState = updateAnswer(clearedFollowups, p.identity, q.id, true);
                                    setFormState(newState);
                                }}
                            />
                            <Radio
                                checked={
                                    (formState &&
                                        formState['answers'][p.identity] &&
                                        formState['answers'][p.identity][q.id] === false) ??
                                    false
                                }
                                name={`q-name-${p.identity}-${q.id}`}
                                label={t('button_no.text')}
                                onClick={() => {
                                    let clearedFollowups = formState;

                                    if (formState && q.deviatingAnswer == 'no') {
                                        const personAnswers = formState.answers[p.identity] ?? {};

                                        const filteredAnswers = Object.fromEntries(
                                            Object.entries(personAnswers).filter(([key]) => {
                                                return !q.followUpQuestions?.some(({id}) => id === Number(key));
                                            })
                                        );

                                        clearedFollowups = {
                                            ...formState,
                                            answers: {...formState.answers, [p.identity]: filteredAnswers}
                                        };
                                    }

                                    const newState = updateAnswer(clearedFollowups, p.identity, q.id, false);
                                    setFormState(newState);
                                }}
                            />
                        </div>
                        <div>
                            {formState &&
                                formState['answers'][p.identity] &&
                                formState['answers'][p.identity][q.id] ===
                                    (q.deviatingAnswer == 'yes' ? true : false) &&
                                q.followUpQuestions &&
                                q.followUpQuestions.length > 0 &&
                                q.followUpQuestions.map((followupQuestion) => {
                                    return (
                                        <div key={followupQuestion.id} className='arrival-dropin-hd-question'>
                                            <div className='arrival-dropin-hd-question__text'>
                                                {getTextFromLocale(followupQuestion.text)}
                                            </div>
                                            <div className='arrival-dropin-hd-question__answer'>
                                                <Radio
                                                    checked={
                                                        (formState &&
                                                            formState['answers'][p.identity] &&
                                                            formState['answers'][p.identity][followupQuestion.id] ===
                                                                true) ??
                                                        false
                                                    }
                                                    name={`q-name-${p.identity}-${followupQuestion.id}`}
                                                    label={t('button_yes.text')}
                                                    onClick={() => {
                                                        const newState = updateAnswer(
                                                            formState,
                                                            p.identity,
                                                            followupQuestion.id,
                                                            true
                                                        );
                                                        setFormState(newState);
                                                    }}
                                                />
                                                <Radio
                                                    checked={
                                                        (formState &&
                                                            formState['answers'][p.identity] &&
                                                            formState['answers'][p.identity][followupQuestion.id] ===
                                                                false) ??
                                                        false
                                                    }
                                                    name={`q-name-${p.identity}-${followupQuestion.id}`}
                                                    label={t('button_no.text')}
                                                    onClick={() => {
                                                        const newState = updateAnswer(
                                                            formState,
                                                            p.identity,
                                                            followupQuestion.id,
                                                            false
                                                        );
                                                        setFormState(newState);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                );
            });

            if (!loadHDClick.has(p.identity) && haveOldHD(p.identity)) {
                oldHDButton = (
                    <div className='arrival-dropin-hd__persons__person__header__oldhd'>
                        <Button big onClick={() => fillOldHD(p.identity)}>
                            {t('arrival.button_old_hd')}
                        </Button>
                    </div>
                );
            }
        } else {
            questions = (
                <div style={{width: '100px', height: '100px', margin: '0 auto'}}>
                    <Spinner />
                </div>
            );
        }

        return (
            <div key={i} className='arrival-dropin-hd__persons__person'>
                <div className='arrival-dropin-hd__persons__person__header'>
                    <div className='arrival-dropin-hd__persons__person__header__name'>
                        <strong>
                            {p.givenName} {p.familyName}
                        </strong>
                        <br />
                        <span>{p.identity}</span>
                    </div>
                    <div className='arrival-dropin-hd__persons__person__header__button'>
                        <Button
                            big
                            variant={allAnswered ? 'outlined' : 'filled'}
                            onClick={() => togglePersonQuestions(p.identity)}
                            className={isVisible ? 'arrow-up' : 'arrow-down'}
                        >
                            {!allAnswered ? t('healthdeclaration.button_fill') : t('healthdeclaration.button_change')}
                        </Button>
                    </div>
                </div>
                <div className={`arrival-dropin-hd__persons__person__body ${!isVisible ? 'hidden' : ''}`}>
                    {oldHDButton}
                    {questions}
                </div>
            </div>
        );
    });

    const allQuestionsAnswered = () => {
        if (!healthDeclaration) return false;

        const allOK = dataState.persons.every((p) => {
            return personAllAnswers(p);
        });

        return allOK;
    };

    return (
        <div className='arrival-dropin-hd'>
            <div>{t('arrival.hd_info')}</div>
            <div className='arrival-dropin-hd__persons'>{persons}</div>
            <DropinTravel />
            <div className='arrival-step-controllers'>
                <div>
                    <Button
                        onClick={() => {
                            dispatch({type: DataActions.SET_STEP, value: 'people'});
                            dispatch({
                                type: DataActions.SET_TRIP,
                                value: {trip: undefined, tripDepartureDate: undefined}
                            });
                        }}
                    >
                        {t('arrival.back')}
                    </Button>
                </div>
                <div>
                    <Button
                        onClick={() => {
                            let formattedTrip:
                                | {date: string; destinations: Array<{countryId: number; duration: number}>}
                                | undefined = undefined;
                            if (dataState.trip && dataState.tripDepartureDate) {
                                formattedTrip = {
                                    date: format(dataState.tripDepartureDate, 'yyyy-MM-dd'),
                                    destinations: dataState.trip as Array<{
                                        countryId: number;
                                        duration: number;
                                    }>
                                };
                            }

                            dataState.persons.forEach((p) => {
                                const answers = formState?.answers?.[p.identity] ?? {};
                                const formattedAnswers = Object.entries(answers).map(([key, value]) => ({
                                    questionId: Number(key),
                                    answer: value === true ? 'yes' : 'no'
                                }));

                                dispatch({
                                    type: DataActions.SET_PERSON_HEALTH_DECL,
                                    value: {
                                        identity: p.identity,
                                        healthDecl: {
                                            definitionId: dataState.reasonState.healthDeclarationDefinitionId,
                                            answers: formattedAnswers,
                                            trip: formattedTrip
                                        }
                                    } as {identity: string; healthDecl: Person['healthDecl']}
                                });
                            });
                            dispatch({type: DataActions.NEXT_STEP});
                        }}
                        disabled={!allQuestionsAnswered()}
                    >
                        {t('arrival.next')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
